import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * Standard hover/focus tooltip for the design system.
 *
 * The sizing + typography is the canonical version of the preview
 * tool-menu tooltip (the reference the rest of the UI standardises on):
 * `w-max max-w-[240px]`, `px-2 py-1`, `text-[11px] leading-snug`,
 * `rounded-[var(--radius-md)]`, hairline border + elevated surface +
 * shadow.
 *
 * Usage — render it as a SIBLING immediately after a `peer` trigger
 * button inside a `relative inline-flex` wrapper; it reveals on the
 * trigger's hover and KEYBOARD focus (focus-visible, so a mouse click
 * doesn't leave it stuck open):
 *
 *   <span className="relative inline-flex">
 *     <button className="peer …">…</button>
 *     <Tooltip>Open in Cursor</Tooltip>
 *   </span>
 *
 * `side` flips it above/below the trigger (default below, since most
 * triggers sit at the top of their region).
 */
export type TooltipSide = 'top' | 'bottom';
/**
 * Horizontal anchoring relative to the trigger. `center` is the default;
 * use `end` for triggers near the RIGHT window edge and `start` near the
 * LEFT edge so the popover opens inward and isn't clipped by the window.
 */
export type TooltipAlign = 'start' | 'center' | 'end';

export function Tooltip({
  children,
  side = 'bottom',
  align = 'center',
  className,
}: {
  children: React.ReactNode;
  side?: TooltipSide;
  align?: TooltipAlign;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      role="tooltip"
      className={cn(
        'pointer-events-none absolute z-30 hidden w-max max-w-[240px] items-center gap-2',
        'rounded-[var(--radius-md)] border border-white/[0.14] bg-[#18151f] px-2 py-1',
        'text-[11px] leading-snug text-white/85 shadow-xl',
        'peer-hover:flex peer-focus-visible:flex',
        side === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2',
        // Anchor inward from the chosen side so the popover never clips the
        // window edge (CSS-only — no JS flip).
        align === 'center' ? 'left-1/2 -translate-x-1/2' : align === 'end' ? 'right-0' : 'left-0',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * The keyboard-shortcut chip shown inside a {@link Tooltip} (e.g.
 * "Cmd + Shift + S"). Mirrors the preview tool-menu tooltip's kbd.
 */
export function TooltipKbd({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <kbd className="shrink-0 whitespace-nowrap rounded border border-white/[0.16] bg-white/[0.06] px-1 py-0.5 font-mono text-[10px] text-white/65">
      {children}
    </kbd>
  );
}
